import React, { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Edit,
  Trash2,
  Calendar,
  DollarSign,
  MapPin,
  FileText,
  Download,
  Eye,
  Tag,
  Image as ImageIcon
} from 'lucide-react'
import { buscarEmendaCompleta, excluirEmenda } from '../../services/emendasService'
import { formatarMoeda, formatarData } from '../../lib/admin-supabase'

interface EmendaCompleta {
  id: string
  titulo: string
  categoria?: string
  resumo?: string
  descricao_completa?: string
  valor_emenda?: number
  municipio_beneficiado?: string
  estado?: string
  status: string
  imagem_capa?: string
  created_at: string
  documentos: Array<{
    id: string
    nome_documento: string
    arquivo_url: string
    tipo_documento: string
  }>
}

export const EmendaDetailProduction: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [emenda, setEmenda] = useState<EmendaCompleta | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (id) {
      carregarEmenda(id)
    }
  }, [id])

  const carregarEmenda = async (emendaId: string) => {
    try {
      setLoading(true)
      const data = await buscarEmendaCompleta(emendaId)
      setEmenda(data)
    } catch (error: any) {
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleExcluir = async () => {
    if (!id || !emenda) return

    if (!confirm(`Tem certeza que deseja excluir a emenda "${emenda.titulo}"? Esta ação não pode ser desfeita.`)) {
      return
    }

    try {
      await excluirEmenda(id)
      navigate('/admin/emendas')
    } catch (error: any) {
      alert(error.message || 'Erro ao excluir emenda')
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ativo': return 'bg-green-100 text-green-800'
      case 'rascunho': return 'bg-yellow-100 text-yellow-800'
      case 'inativo': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error || !emenda) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <Link to="/admin/emendas" className="inline-flex items-center text-sm text-blue-600 hover:underline mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" /> Voltar
        </Link>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-red-700">
          {error || 'Emenda não encontrada'}
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto py-8 px-4">
      {/* Topo */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <Link to="/admin/emendas" className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-blue-600 transition-colors">
            <ArrowLeft className="h-4 w-4 mr-2" /> Voltar para lista
          </Link>
          <h1 className="text-3xl font-extrabold text-gray-900 mt-2">{emenda.titulo}</h1>
          <div className="flex items-center gap-3 mt-3">
            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${getStatusColor(emenda.status)}`}>
              {emenda.status}
            </span>
            {emenda.categoria && (
              <span className="flex items-center text-sm text-gray-500 font-medium">
                <Tag className="h-4 w-4 mr-1" /> {emenda.categoria}
              </span>
            )}
            <span className="text-sm text-gray-400">
              ID: {emenda.id.split('-')[0]}...
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to={`/admin/emendas/${id}/editar`}
            className="inline-flex items-center px-5 py-2.5 bg-white border border-gray-300 rounded-xl text-sm font-bold text-gray-700 hover:bg-gray-50 transition-all shadow-sm"
          >
            <Edit className="h-4 w-4 mr-2" /> Editar
          </Link>
          <button
            onClick={handleExcluir}
            className="inline-flex items-center px-5 py-2.5 bg-red-50 text-red-700 rounded-xl text-sm font-bold hover:bg-red-100 transition-all"
          >
            <Trash2 className="h-4 w-4 mr-2" /> Excluir
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Coluna Lateral */}
        <div className="lg:col-span-4 space-y-6">
          {/* Card da Foto */}
          <div className="bg-white rounded-3xl shadow-sm border border-gray-200 overflow-hidden p-3">
            <div className="aspect-square rounded-2xl overflow-hidden bg-gray-100">
              {emenda.imagem_capa ? (
                <img src={emenda.imagem_capa} alt={emenda.titulo} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-300">
                  <ImageIcon className="h-16 w-16" />
                </div>
              )}
            </div>
          </div>

          {/* Atributos Rápidos */}
          <div className="bg-white rounded-3xl shadow-sm border border-gray-200 p-6 space-y-6">
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 block mb-1">Valor da Emenda</label>
              <div className="text-2xl font-black text-emerald-600">{formatarMoeda(emenda.valor_emenda)}</div>
            </div>

            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-blue-500 shrink-0" />
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 block">Cidade / UF</label>
                <div className="text-sm font-bold text-gray-700">{emenda.municipio_beneficiado || 'Não informado'} - {emenda.estado || 'BR'}</div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-blue-500 shrink-0" />
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 block">Criado em</label>
                <div className="text-sm font-bold text-gray-700">{formatarData(emenda.created_at)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Conteúdo Principal */}
        <div className="lg:col-span-8 space-y-6">
          {/* Descrição e Texto */}
          <div className="bg-white rounded-3xl shadow-sm border border-gray-200 p-8 md:p-10">
            {emenda.resumo && (
              <div className="mb-8">
                <h3 className="text-sm font-black uppercase tracking-widest text-blue-600 mb-3 ml-1">Descrição (Resumo)</h3>
                <p className="text-lg text-gray-600 font-medium leading-relaxed bg-blue-50/50 p-6 rounded-2xl border-l-4 border-blue-400 italic">
                  {emenda.resumo}
                </p>
              </div>
            )}

            <div>
              <h3 className="text-sm font-black uppercase tracking-widest text-gray-400 mb-4 ml-1">Texto Completo</h3>
              <div className="text-gray-700 leading-loose whitespace-pre-wrap text-base">
                {emenda.descricao_completa || <span className="text-gray-400 italic">Nenhum texto completo cadastrado.</span>}
              </div>
            </div>
          </div>

          {/* Documentos */}
          {emenda.documentos.length > 0 && (
            <div className="bg-white rounded-3xl shadow-sm border border-gray-200 p-8 md:p-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-blue-600 rounded-lg">
                  <FileText className="h-5 w-5 text-white" />
                </div>
                <h2 className="text-xl font-black text-gray-900">Documentos PDF</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {emenda.documentos.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-4 bg-gray-50 border border-gray-100 rounded-2xl group hover:border-blue-200 hover:bg-blue-50/30 transition-all">
                    <div className="flex items-center gap-3 overflow-hidden">
                      <Download className="h-5 w-5 text-gray-400 group-hover:text-blue-500" />
                      <span className="text-sm font-bold text-gray-700 truncate">{doc.nome_documento}</span>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <a href={doc.arquivo_url} target="_blank" rel="noopener noreferrer" className="p-2 text-gray-400 hover:text-blue-600"><Eye className="h-4 w-4" /></a>
                      <a href={doc.arquivo_url} download className="p-2 text-gray-400 hover:text-emerald-600"><Download className="h-4 w-4" /></a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
